import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import ReactTable from "components/lib/Global/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import { getFullName, getMaskedEmail } from "utils";
import AvatarIcon from "components/lib/Shared/Icons/AvatarIcon";
import { Constants } from "config/constants";
import SubRowAsync from "../SubRowAsync";
import ChangeOfParticularsSubRow from "components/CompanyDashboard/MaintainYourCompany/Changes/OfficialsShareholdersAuthorisedPersons/ChangeOfParticulars/ChangeOfParticularsSubRow";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import { useContext } from "react";

const JointShareholderTable = (props) => {
  const { jointShareholders } = props;
  const accessRequests = useSelector(
    (state) => state.accessRequestsSlice.items
  );
  const { company } = useSelector((state) => state.companySlice);
  const currentUser = useSelector((state) => state.userSlice);
  const role = currentUser?.role;
  const params = useParams();
  const companyId = company?._id ?? params?.companyId;
  const originalCompanyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );
  const { beneficialOwners } = company;
  const dispatch = useDispatch();
  const [changeOfParticularsIsSelected, setChangeOfParticularsIsSelected] =
    useState(false);
  const { changeOfParticularsInfo, setChangeOfParticularsInfo } =
    useContext(ManageModuleContext);

  let onClick = (row) => {
    if (!row.isExpanded) {
      row.toggleAllRowsExpanded(false);
      row.toggleRowExpanded();
    } else {
      row.toggleRowExpanded();
    }
  };

  const handleChange = (row) => {
    setChangeOfParticularsInfo({
      ...changeOfParticularsInfo,
      selectedValueType: "changes-including-email",
    });

    setChangeOfParticularsIsSelected(true);
    onClick(row);
  };

  const handleClose = (row) => {
    setChangeOfParticularsIsSelected(false);
    onClick(row);
  };

  const columns = [
    {
      Header: "Full Name",
      accessor: "fullName",
      className: "p-4 text-ellipsis overflow-hidden max-w-[150px]",
      Cell: ({ row: { original } }) => {
        const { names, fileUploads, electronicAddress, initiatedToEmail } =
          original;
        const fullName = getFullName(names);
        const emailAddress =
          electronicAddress?.emailAddress ?? initiatedToEmail ?? "";
        return (
          <div className="flex items-center space-x-1">
            <div className={`flex-shrink-0`}>
              {fileUploads?.passportPhoto ? (
                <img
                  className="w-8 h-8 rounded-full"
                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${fileUploads?.passportPhoto}`}
                  alt={fullName}
                />
              ) : (
                <div className="w-8 h-8 rounded-full">
                  <div className="relative w-8 h-8 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600">
                    <AvatarIcon />
                  </div>
                </div>
              )}
            </div>
            <div className="px-3">
              <h1 className="font-bold">{fullName}</h1>
              <p>
                {original.type === "invited"
                  ? getMaskedEmail(emailAddress ?? "")
                  : emailAddress}
              </p>
            </div>
          </div>
        );
      },
    },
    { Header: "ID Type", accessor: "idType" },
    { Header: "Is a Director?", accessor: "isDirector" },
    {
      Header: "Action",
      Cell: ({ row }) => {
        const isFirstRow =
          jointShareholders.findIndex(
            (jointShareholder) => jointShareholder._id === row.original._id
          ) === 0;

        const isOwnerRow = [
          row.original?.electronicAddress?.emailAddress,
          row.original?.identificationDetails?.emailAddress,
          row.original?.identificationDetails?.guardianEmailAddress,
        ].includes(currentUser.electronicAddress.emailAddress);

        const disableEditBtn =
          (row.original?.type === "invited" ||
            role === Constants.COOKIE_ROLES.OFFICIAL) &&
          !isOwnerRow;

        if (row?.isExpanded) {
          return (
            <TableActionMenu
              row={row}
              showCloseBtn={true}
              hideEditBtn={true}
              hideDeleteBtn={true}
              handleClose={handleClose}
            />
          );
        }

        return (
          <TableActionMenu
            row={row}
            showChange={true}
            handleChange={handleChange}
            hideDeleteBtn={true}
            hideEditBtn={true}
          />
        );
      },
    },
  ];

  // Create a function that will render our row sub components
  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => {
      if (
        changeOfParticularsIsSelected ||
        (row?.original?.changeOfParticularDate && editModeIsSelected)
      ) {
        const boOfficial = beneficialOwners.find(
          (bo) =>
            bo?.names?.firstName === row.original?.names?.firstName &&
            bo?.names?.lastName === row.original?.names?.lastName
        );
        return (
          <ChangeOfParticularsSubRow
            row={row}
            visibleColumns={visibleColumns}
            changeList={[]}
            setChangeList={() => null}
            actionType=""
            boOfficial={boOfficial}
            isJointShareholder={true}
          />
        );
      }
      return (
        <SubRowAsync
          row={{
            ...row,
            original: originalCompanyOfficials.find(
              (official) => official._id === row.original._id
            ),
          }}
          officialIndex={originalCompanyOfficials.findIndex(
            (official) => official._id === row.original._id
          )}
          syncChangesToBOTab
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          currentTab={"stakeholderTab"}
        />
      );
    },
    [originalCompanyOfficials, changeOfParticularsIsSelected]
  );

  return (
    <>
      <h2 className="mb-2 font-semibold">Joint Shareholder Appointments</h2>
      <div className="w-full bg-white shadow">
        <div className="w-full overflow-x-auto">
          <ReactTable
            columns={columns}
            data={jointShareholders}
            renderRowSubComponent={renderRowSubComponent}
          />
        </div>
      </div>
    </>
  );
};

export default JointShareholderTable;
